import { Injectable } from '@angular/core';
import { AccountingDataProvider } from '../tokens';
import { OfflineStateService } from './offline-state.service';
import { OfflineSequenceService } from './offline-sequence.service';
import type {
  OfflineChartOfAccount, JournalEntryFilterParams, OfflineJournalEntry,
  CreateJournalEntryData, OfflineFiscalYear, SuccessResult,
} from '../types/offline-models';

@Injectable({ providedIn: 'root' })
export class OfflineAccountingService implements AccountingDataProvider {
  constructor(
    private offlineState: OfflineStateService,
    private sequenceService: OfflineSequenceService,
  ) {}

  async getChartOfAccounts(): Promise<OfflineChartOfAccount[]> {
    const db = this.offlineState.getDb();
    if (!db) throw new Error('Offline DB not initialized');
    const docs = await db.chart_of_accounts.find({ selector: { isActive: true } }).exec();
    return docs.map(d => d.toJSON() as OfflineChartOfAccount);
  }

  async getJournalEntries(params: JournalEntryFilterParams): Promise<OfflineJournalEntry[]> {
    const db = this.offlineState.getDb();
    if (!db) throw new Error('Offline DB not initialized');
    const selector: Record<string, string | number> = {};
    if (params?.fiscalYearId) selector['fiscalYearId'] = params.fiscalYearId;
    if (params?.status) selector['status'] = params.status;
    const docs = await db.journal_entries.find({ selector }).exec();
    return docs.map(d => d.toJSON() as OfflineJournalEntry);
  }

  async createJournalEntry(data: CreateJournalEntryData): Promise<SuccessResult> {
    const db = this.offlineState.getDb();
    if (!db) throw new Error('Offline DB not initialized');

    const entryNumber = await this.sequenceService.generateJournalEntryNumber(data.userId);
    const now = new Date().toISOString();

    let totalDebit = 0;
    let totalCredit = 0;
    for (const line of data.lines) {
      totalDebit += line.debit;
      totalCredit += line.credit;
    }

    const entry = {
      id: String(Date.now()),
      entryNumber,
      fiscalYearId: data.fiscalYearId,
      fiscalPeriodId: null,
      entryDate: data.entryDate,
      description: data.description ?? null,
      source: 'MANUAL',
      status: 'DRAFT',
      journalCode: data.journalCode ?? null,
      currencyCode: null,
      exchangeRate: null,
      totalDebit,
      totalCredit,
      lines: data.lines,
      companyId: data.companyId,
      offlineId: crypto.randomUUID(),
      syncStatus: 'PENDING',
      createdAt: now,
      createdBy: data.userId,
    };

    await db.journal_entries.insert(entry);
    await this.offlineState.refreshPendingCount();
    return { success: true, entryNumber };
  }

  async getFiscalYears(): Promise<OfflineFiscalYear[]> {
    const db = this.offlineState.getDb();
    if (!db) throw new Error('Offline DB not initialized');
    const docs = await db.fiscal_years.find().exec();
    return docs.map(d => d.toJSON() as OfflineFiscalYear);
  }
}
